import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import LanguageModal from '../components/LanguageModal';
import CassetteLogo from '../components/CassetteLogo';
import SeoHead from '../components/SeoHead';
import { MUSIC_SEO } from '../seo/seoConfig';
import { faqPageJsonLd } from '../seo/jsonLd';
import { buildHelpFaqItems } from '../seo/helpFaq';
import ga from '../services/analytics';
import { SketchPageShell, SketchNavBar } from '../components/sketchy';

const Help = () => {
  const { t } = useTranslation();
  const [languageOpen, setLanguageOpen] = useState(false);
  const faqItems = buildHelpFaqItems(t);

  // Track help page view when component mounts
  useEffect(() => {
    ga.event('help_page_viewed');
  }, []);

  return (
    <SketchPageShell className="min-h-screen flex flex-col">
      <SeoHead meta={MUSIC_SEO.help} jsonLd={faqPageJsonLd(faqItems)} />
      <SketchNavBar>
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between h-16 items-center">
            {/* Left: Back Button + Logo + Text */}
            <div className="flex items-center gap-3">
              <Link 
                to="/" 
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[var(--color-hover)] transition-colors group"
              >
                <ArrowLeftIcon className="h-5 w-5 group-hover:-translate-x-1 transition-transform duration-200" />
                <span className="text-sm font-medium">{t('buttons.back')}</span>
              </Link>
              <Link
                to="/"
                className="flex items-center gap-2 p-2 rounded-lg hover:bg-[var(--color-hover)] transition-colors cursor-pointer"
                aria-label="cassette.diy - Go to Dashboard"
              >
                <CassetteLogo className="w-8 h-6 sm:w-10 sm:h-7" />
                <span className="text-lg sm:text-xl font-bold text-[var(--color-text)] hover:text-[var(--brand-from)] transition-colors hidden sm:inline">cassette.diy</span>
              </Link>
            </div>
            {/* Right: Language button */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => setLanguageOpen(true)}
                className="px-3 py-2 text-sm rounded-lg hover:bg-[var(--color-hover)] transition-colors text-[var(--color-text)]"
              >
                🌐 {t('menu.language')}
              </button>
            </div>
          </div>
        </div>
      </SketchNavBar>
      
      {/* Main Content */}
      <main className="flex-1 py-6 px-4 sm:px-6 lg:px-8 text-[var(--color-text)]">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--color-text)' }}>{t('help.title')}</h1>
            <p style={{ color: 'var(--color-muted)' }}>{t('help.intro')}</p>
          </div>

          <h2 className="text-lg font-semibold mt-6 mb-2" style={{ color: 'var(--color-text)' }}>{t('help.whatIsCassette.title')}</h2>
          <p className="mb-4" style={{ color: 'var(--color-text)' }}>{t('help.whatIsCassette.body')}</p>

          <h2 className="text-lg font-semibold mt-6 mb-2" style={{ color: 'var(--color-text)' }}>{t('help.whatYouCanDo.title')}</h2>
          <ul className="list-disc ml-6 mb-4" style={{ color: 'var(--color-text)' }}>
            <li>{t('help.whatYouCanDo.createTrack')}</li>
            <li>{t('help.whatYouCanDo.uploadAudio')}</li>
            <li>{t('help.whatYouCanDo.manageArtwork')}</li>
            <li>{t('help.whatYouCanDo.addPromo')}</li>
            <li>{t('help.whatYouCanDo.trackProgress')}</li>
            <li>{t('help.whatYouCanDo.organizeFiles')}</li>
            <li>{t('help.whatYouCanDo.stayMotivated')}</li>
          </ul>

          <h2 className="text-lg font-semibold mt-6 mb-2" style={{ color: 'var(--color-text)' }}>{t('help.gettingStarted.title')}</h2>
          <ol className="list-decimal ml-6 mb-4" style={{ color: 'var(--color-text)' }}>
            <li>{t('help.gettingStarted.step1')}</li>
            <li>{t('help.gettingStarted.step2')}</li>
            <li>{t('help.gettingStarted.step3')}</li>
          </ol>

          <h2 className="text-lg font-semibold mt-6 mb-2" style={{ color: 'var(--color-text)' }}>{t('help.faq.title')}</h2>
          <div className="space-y-4">
            {faqItems.map((item) => (
              <div key={item.question}>
                <h3 className="font-semibold" style={{ color: 'var(--color-text)' }}>{item.question}</h3>
                <p className="text-sm mt-1" style={{ color: 'var(--color-muted)' }}>{item.answer}</p>
              </div>
            ))}
          </div>

          <p className="mt-6 text-sm">
            <Link to="/guides/music-release-workflow" className="text-[var(--brand-from)] hover:underline">
              Read our indie release workflow guide
            </Link>
          </p>

          <h2 className="text-lg font-semibold mt-6 mb-2" style={{ color: 'var(--color-text)' }}>{t('help.otherTools.title')}</h2>
          <p className="mb-4" style={{ color: 'var(--color-text)' }}>{t('help.otherTools.description')}</p>
          
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold" style={{ color: 'var(--color-text)' }}>{t('help.otherTools.daw.title')}</h3>
              <p className="text-sm mt-1" style={{ color: 'var(--color-muted)' }}>{t('help.otherTools.daw.description')}</p>
              <p className="text-sm font-medium mt-1" style={{ color: 'var(--color-text)' }}>{t('help.otherTools.daw.tools')}</p>
            </div>
            
            <div>
              <h3 className="font-semibold" style={{ color: 'var(--color-text)' }}>{t('help.otherTools.distribution.title')}</h3>
              <p className="text-sm mt-1" style={{ color: 'var(--color-muted)' }}>{t('help.otherTools.distribution.description')}</p>
              <p className="text-sm font-medium mt-1" style={{ color: 'var(--color-text)' }}>{t('help.otherTools.distribution.services')}</p>
            </div>
            
            <div>
              <h3 className="font-semibold" style={{ color: 'var(--color-text)' }}>{t('help.otherTools.promotion.title')}</h3>
              <p className="text-sm mt-1" style={{ color: 'var(--color-muted)' }}>{t('help.otherTools.promotion.description')}</p>
              <p className="text-sm font-medium mt-1" style={{ color: 'var(--color-text)' }}>{t('help.otherTools.promotion.platforms')}</p>
            </div>
          </div>

          <h2 className="text-lg font-semibold mt-6 mb-2" style={{ color: 'var(--color-text)' }}>{t('help.contact.title')}</h2>
          <p style={{ color: 'var(--color-text)' }}>{t('help.contact.body')}</p>
        </div>
      </main>

      {/* Language Modal */}
      <LanguageModal open={languageOpen} onClose={() => setLanguageOpen(false)} />
    </SketchPageShell>
  );
};

export default Help; 